import React from 'react';
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MyButtonToggle, MyCheckBoxLabel, CollapsiblePanel, MyInputLabel, Hr, Vr } from '../../component/ui/UI.jsx';
import { useSave } from '../../context/SaveContext.jsx';
import { useApp } from '../../context/AppContext.jsx';
import { getEndpoint } from '../../context/AppContext.jsx';

import { WhiteSymbol, BlueSymbol, BlackSymbol, RedSymbol, GreenSymbol, ColorlessSymbol } from "../../component/mtg/CardSymbol.jsx";
import { CardTypeList, SupertypeList, CardRarityList, CardKeywordList, FrameEffectList } from '../../component/mtg/CardSymbol.jsx';
import { ArtifactTypeList, EnchantmentTypeList, LandTypeList, SpellTypeList, CreatureTypeList, BattleTypeList } from '../../component/mtg/CardSymbol.jsx';
import { CardLanguage, CardAvailabilityList } from '../../component/mtg/CardSymbol.jsx';

const FilterGroup = ({children}) => {
	return (
		<div className="d-flex align-items-center flex-wrap gap-1">
			{children}
		</div>
	);
};

export const SearchSetting = () =>
{
	const { t, i18n } = useTranslation();
	const {
		searchArtifactTypePanel, searchEnchantmentTypePanel, searchLandTypePanel, searchSpellTypePanel, searchCreatureTypePanel, searchBattleTypePanel,
		searchKeytypePanel, searchSetPanel, searchFrameEffectPanel, searchFrameEffectExcludePanel,
		cardSets, getCardSets,
		setCardSearchCache
	} = useApp();

	const {filters:saveFilters, setFilters:saveSetFilters} = useSave();
	const [filters, setFilters] = useState(saveFilters);

	const onChangeLanguage = (e, key) => {
		setFilters((prev) => ({
			...prev,
			[key]: e.target.value,
		}));
	};

	const onChangeToggle = (e, key) => {
		const { name, checked } = e.target;
		setFilters((prev) => ({
			...prev,
			[key]: checked,
		}));
	};

	const onChangeFormat = (e) => {
		setFilters((prev) => ({
			...prev,
			format: e.target.value,
		}));
		e.target.blur();
	};

	useEffect(() => {
		getCardSets(i18n.language);
	}, [i18n.language]);

	const selectToggles = (field, key) => {
		return filters[field].includes(key);
	};
	const setToggles = (field, key) => {
		setFilters((prev) => ({
			...prev,
			[field]: prev[field].includes(key) ? prev[field].filter(i => i !== key) : [...prev[field], key]
		}));
	};
	const getSummary = (prefix, field) => {
		return filters[field].map(v => t(`${prefix}${v}`)).join(", ");
	}
	const getSummarySets = (field) => {
		return filters[field].map(v => (cardSets.find(set => set.code === v)?.name ?? v)).join(", ");
	}

	useEffect(() => {
		if (saveFilters !== filters)
		{
			setCardSearchCache(null);
		}
		saveSetFilters(filters);
	}, [filters]);

	return (
		<div
			className="container-fluid h-100 p-0"
			style={{overflowY: "auto", overflowX: "hidden"}}
		>
			<div className="d-flex flex-column p-1 gap-1 overflow-y-auto">
				<FilterGroup>
					<MyInputLabel
						placeholder={t("Card_Name")}
						value={filters.name}
						setValue={(v) => {
							setFilters((prev) => ({...prev, name: v}));
						}}
					/>
					<CardLanguage value={filters.searchLanguage} onChange={(e) => {onChangeLanguage(e, "searchLanguage")}} />
					<Vr />
					<CardLanguage value={filters.language} onChange={(e) => {onChangeLanguage(e, "language")}} />
					<Vr />
					{CardAvailabilityList().map((item, index) => 
						<MyButtonToggle 
							key={index}
							text={item.value}
							checked={selectToggles("availability", item.key)}
							onChange={() => setToggles("availability", item.key)}
						/>
					)}
				</FilterGroup>
				<Hr />
				<FilterGroup>
					<div>{t("Card_ColorIdentity")}</div>
					<WhiteSymbol checked={selectToggles("colors", "W")} onChange={() => setToggles("colors", "W")} />
					<BlueSymbol checked={selectToggles("colors", "U")} onChange={() => setToggles("colors", "U")} />
					<BlackSymbol checked={selectToggles("colors", "B")} onChange={() => setToggles("colors", "B")} />
					<RedSymbol checked={selectToggles("colors", "R")} onChange={() => setToggles("colors", "R")} />
					<GreenSymbol checked={selectToggles("colors", "G")} onChange={() => setToggles("colors", "G")} />
					<ColorlessSymbol checked={selectToggles("colors", "C")} onChange={() => setToggles("colors", "C")} />
					<MyCheckBoxLabel name="AND" value={filters.colorsAnd} onChange={(e)=>{onChangeToggle(e, "colorsAnd")}}/>
					<Vr />
					<div>{t("Common_Exclude")}</div>
					<WhiteSymbol checked={selectToggles("colorsExclude", "W")} onChange={() => setToggles("colorsExclude", "W")} />
					<BlueSymbol checked={selectToggles("colorsExclude", "U")} onChange={() => setToggles("colorsExclude", "U")} />
					<BlackSymbol checked={selectToggles("colorsExclude", "B")} onChange={() => setToggles("colorsExclude", "B")} />
					<RedSymbol checked={selectToggles("colorsExclude", "R")} onChange={() => setToggles("colorsExclude", "R")} />
					<GreenSymbol checked={selectToggles("colorsExclude", "G")} onChange={() => setToggles("colorsExclude", "G")} />
					<ColorlessSymbol checked={selectToggles("colorsExclude", "C")} onChange={() => setToggles("colorsExclude", "C")} />
				</FilterGroup>
				<Hr />
				<FilterGroup>
					{CardTypeList().map((item, index) =>
						<MyButtonToggle
							key={index}
							text={item.value}
							checked={selectToggles("types", item.key)}
							onChange={() => setToggles("types", item.key)}
						/>
					)}
					<MyCheckBoxLabel name="AND" value={filters.typesAnd} onChange={(e)=>{onChangeToggle(e, "typesAnd")}}/>
				</FilterGroup>
				<Hr />
				<FilterGroup>
					{SupertypeList().map((item, index) =>
						<MyButtonToggle
							key={index}
							text={item.value}
							checked={selectToggles("supertype", item.key)}
							onChange={() => setToggles("supertype", item.key)}
						/>
					)}
				</FilterGroup>
				<Hr />
				<FilterGroup>
					{CardRarityList().map((item, index) => 
						<MyButtonToggle 
							key={index}
							text={item.value}
							checked={selectToggles("rarity", item.key)}
							onChange={() => setToggles("rarity", item.key)}
						/>
					)}
				</FilterGroup>
				<Hr />
				<CollapsiblePanel state={searchArtifactTypePanel} text={t("Card_ArtifactType")} summary={getSummary("Subtype_", "artifactTypes")}>
					<FilterGroup>
						{ArtifactTypeList().map((item, index) => 
							<MyButtonToggle 
								key={index}
								text={item.value}
								checked={selectToggles("artifactTypes", item.key)}
								onChange={() => setToggles("artifactTypes", item.key)}
							/>
						)}
					</FilterGroup>
				</CollapsiblePanel>
				<Hr />
				<CollapsiblePanel state={searchEnchantmentTypePanel} text={t("Card_EnchantmentType")} summary={getSummary("Subtype_", "enchantmentTypes")}>
					<FilterGroup>
						{EnchantmentTypeList().map((item, index) => 
							<MyButtonToggle 
								key={index}
								text={item.value}
								checked={selectToggles("enchantmentTypes", item.key)}
								onChange={() => setToggles("enchantmentTypes", item.key)}
							/>
						)}
					</FilterGroup>
				</CollapsiblePanel>
				<Hr />
				<CollapsiblePanel state={searchLandTypePanel} text={t("Card_LandType")} summary={getSummary("Subtype_", "landTypes")}>
					<FilterGroup>
						{LandTypeList().map((item, index) => 
							<MyButtonToggle 
								key={index}
								text={item.value}
								checked={selectToggles("landTypes", item.key)}
								onChange={() => setToggles("landTypes", item.key)}
							/>
						)}
					</FilterGroup>
				</CollapsiblePanel>
				<Hr />
				<CollapsiblePanel state={searchSpellTypePanel} text={t("Card_SpellType")} summary={getSummary("Subtype_", "spellTypes")}>
					<FilterGroup>
						{SpellTypeList().map((item, index) => 
							<MyButtonToggle 
								key={index}
								text={item.value}
								checked={selectToggles("spellTypes", item.key)}
								onChange={() => setToggles("spellTypes", item.key)}
							/>
						)}
					</FilterGroup>
				</CollapsiblePanel>
				<Hr />
				<CollapsiblePanel state={searchCreatureTypePanel} text={t("Card_CreatureType")} summary={getSummary("Subtype_", "creatureTypes")}>
					<FilterGroup>
						{CreatureTypeList().map((item, index) => 
							<MyButtonToggle 
								key={index}
								text={item.value}
								checked={selectToggles("creatureTypes", item.key)}
								onChange={() => setToggles("creatureTypes", item.key)}
							/>
						)}
					</FilterGroup>
				</CollapsiblePanel>
				<Hr />
				<CollapsiblePanel state={searchBattleTypePanel} text={t("Card_BattleType")} summary={getSummary("Subtype_", "battleTypes")}>
					<FilterGroup>
						{BattleTypeList().map((item, index) => 
							<MyButtonToggle 
								key={index}
								text={item.value}
								checked={selectToggles("battleTypes", item.key)}
								onChange={() => setToggles("battleTypes", item.key)}
							/>
						)}
					</FilterGroup>
				</CollapsiblePanel>
				<Hr />
				<CollapsiblePanel state={searchKeytypePanel} text={t("Card_KeywordAbility")} summary={getSummary("Keyword_", "keywords")}>
					<FilterGroup>
						{CardKeywordList().map((item, index) => 
							<MyButtonToggle 
								key={index}
								text={item.value}
								checked={selectToggles("keywords", item.key)}
								onChange={() => setToggles("keywords", item.key)}
							/>
						)}
					</FilterGroup>
				</CollapsiblePanel>
				<Hr />
				<CollapsiblePanel state={searchFrameEffectPanel} text={t("Card_FrameEffect")} summary={getSummary("FrameEffect_", "frameEffect")}>
					<FilterGroup>
						{FrameEffectList().map((item, index) => 
							<MyButtonToggle 
								key={index}
								text={item.value}
								checked={selectToggles("frameEffect", item.key)}
								onChange={() => setToggles("frameEffect", item.key)}
							/>
						)}
					</FilterGroup>
				</CollapsiblePanel>
				<CollapsiblePanel state={searchFrameEffectExcludePanel} text={t("Common_Exclude")} summary={getSummary("FrameEffect_", "frameEffectExclude")}>
					<FilterGroup>
						{FrameEffectList().map((item, index) => 
							<MyButtonToggle 
								key={index}
								text={item.value}
								checked={selectToggles("frameEffectExclude", item.key)}
								onChange={() => setToggles("frameEffectExclude", item.key)}
							/>
						)}
					</FilterGroup>
				</CollapsiblePanel>
				<Hr />
				<FilterGroup>
					{/*
					<MySelectBoxLabel
						name="format"
						itemList={[
							{value: "None", text: "None"},
							{value: "Commander", text: "Commander"},
							{value: "Standart", text: "Standart"},
							{value: "Pioneer", text: "Pioneer"},
							{value: "Modern", text: "Modern"},
							{value: "Legacy", text: "Legacy"},
							{value: "Vintage", text: "Vintage"},
						]}
						value={filters.format}
						onChange={onChangeFormat}
					/>
					*/}
				</FilterGroup>
				<CollapsiblePanel state={searchSetPanel} text={t("Card_Set")} summary={getSummarySets("sets")}>
					<FilterGroup>
						{cardSets.map((set, index) => 
							<MyButtonToggle 
								key={index}
								text={set.name}
								checked={selectToggles("sets", set.code)}
								onChange={() => setToggles("sets", set.code)}
							/>
						)}
					</FilterGroup>
				</CollapsiblePanel>
			</div>
		</div>
	);
};
